import { useState, useEffect } from "react";
import { ShoppingBag } from "lucide-react";

interface SplashScreenProps {
  onComplete: () => void;
}

export function SplashScreen({ onComplete }: SplashScreenProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"enter" | "loading" | "exit">("enter");

  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    // Phase transitions
    const phaseTimer = setTimeout(() => setPhase("loading"), 100);
    const exitTimer = setTimeout(() => setPhase("exit"), 2500);
    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 3200);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(phaseTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed inset-0 z-9999 overflow-hidden
        bg-neutral-900
        flex items-center justify-center
        transition-all duration-500 ease-in-out
        ${phase === "exit" ? "opacity-0" : "opacity-100"}
      `}
    >
      {/* Simple Background Gradient */}
      <div className="absolute inset-0 bg-linear-to-br from-neutral-900 via-neutral-900 to-primary/10" />

      {/* Subtle Corner Accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-neutral-800/10 rounded-full blur-3xl" />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center space-y-12 px-6">
        {/* Logo Container */}
        <div
          className={`
            relative transition-all duration-700 ease-out
            ${phase === "loading" ? "opacity-100 scale-100" : "opacity-0 scale-95"}
          `}
        >
          {/* Logo Card - Clean Design */}
          <div className="relative bg-neutral-800/50 backdrop-blur-xl p-10 rounded-3xl shadow-2xl border border-neutral-800/50 ring-1 ring-white/5">
            {/* Subtle Gold Accent */}
            <div className="absolute inset-0 bg-linear-to-br from-primary/10 to-transparent rounded-3xl" />

            {/* Logo */}
            <div className="relative">
              <img
                src="/assets/Images/logo/blackstore.png"
                alt="BlackStore"
                className="w-40 h-40 mx-auto object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />

              {/* Fallback Logo */}
              <div
                className="hidden w-40 h-40 mx-auto bg-linear-to-br from-neutral-800 to-neutral-900 rounded-2xl items-center justify-center relative"
                style={{ display: "none" }}
              >
                <ShoppingBag
                  className="w-20 h-20 text-primary"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Brand Name */}
        <div
          className={`
            text-center space-y-6 transition-all duration-700 delay-100
            ${phase === "loading" ? "opacity-100" : "opacity-0"}
          `}
        >
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight">
            BLACKSTORE
          </h1>

          {/* Simple Divider */}
          <div className="w-24 h-1 bg-linear-to-r from-primary via-primary-light to-primary mx-auto rounded-full" />

          <p className="text-neutral-400 text-base font-medium tracking-wide">
            Premium Shopping Experience
          </p>
        </div>

        {/* Progress Bar Container */}
        <div
          className={`
            w-80 max-w-full transition-all duration-700 delay-200
            ${phase === "loading" ? "opacity-100" : "opacity-0"}
          `}
        >
          {/* Progress Bar */}
          <div className="relative h-1 bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-primary shadow-[0_0_10px_rgba(255,215,0,0.3)] transition-all duration-200 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Progress Text */}
          <div className="flex items-center justify-center mt-3">
            <span className="text-neutral-500 font-mono text-xs tracking-wider">
              {progress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SplashScreen;
